"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { LayoutGrid, List } from "lucide-react";
import type { LandingExam } from "../landing-data";
import { type ExamStatus } from "@/lib/exam/config";
import { statusOf, betaCaveat, StatusDot } from "@/components/catalog/status";

function StatusPill({ status }: { status: ExamStatus }) {
  if (status === "beta") {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
        Beta
      </span>
    );
  }
  if (status === "coming_soon") {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
        Soon
      </span>
    );
  }
  return <StatusDot />;
}

const CATEGORIES: Array<{ id: string; label: string; blurb: string }> = [
  { id: "all",          label: "All exams",     blurb: "Browse every available test"          },
  { id: "academic",     label: "Academic",      blurb: "College & graduate admissions"        },
  { id: "english",      label: "English",       blurb: "TOEFL, IELTS & proficiency tests"     },
  { id: "medical",      label: "Medical",       blurb: "Licensing for healthcare professionals" },
  { id: "professional", label: "Professional",  blurb: "Industry & financial certifications"  },
  { id: "licensing",    label: "Licensing",     blurb: "State & federal license exams"        },
  { id: "language",     label: "Languages",     blurb: "Foreign language proficiency"         },
];

interface Props {
  exams: LandingExam[];
  /** Preselected category (from /exams?category=…, e.g. via the nav dropdown). */
  initialCategory?: string;
}

const STATUS_ORDER: Record<ExamStatus, number> = { live: 0, beta: 1, coming_soon: 2, hidden: 3 };

export function ExamBrowseGrid({ exams, initialCategory }: Props) {
  const [category, setCategory] = useState<string>(
    initialCategory && ["academic", "english", "medical", "professional", "licensing", "language"].includes(initialCategory)
      ? initialCategory
      : "all",
  );
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const counts = useMemo(() => {
    const m: Record<string, number> = { all: exams.length };
    for (const e of exams) m[e.category] = (m[e.category] ?? 0) + 1;
    return m;
  }, [exams]);

  const filtered = useMemo(() => {
    let xs = exams;
    if (category !== "all") xs = xs.filter(e => e.category === category);
    if (query.trim()) {
      const q = query.toLowerCase();
      xs = xs.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.abbr.toLowerCase().includes(q) ||
        e.desc.toLowerCase().includes(q)
      );
    }
    // Sort by readiness (complete → beta → coming soon), stable within tiers
    return [...xs].sort((a, b) => STATUS_ORDER[statusOf(a.slug)] - STATUS_ORDER[statusOf(b.slug)]);
  }, [exams, category, query]);

  // Tier groups for the sectioned grid view
  const tiers = useMemo(() => ({
    live: filtered.filter(e => statusOf(e.slug) === "live"),
    beta: filtered.filter(e => statusOf(e.slug) === "beta"),
    soon: filtered.filter(e => statusOf(e.slug) === "coming_soon"),
  }), [filtered]);

  const activeCat = CATEGORIES.find(c => c.id === category) ?? CATEGORIES[0];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
      {/* ── Sidebar: category filter ── */}
      <aside className="lg:sticky lg:top-24 self-start">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-muted)] mb-3 px-1">
          Categories
        </div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-6 px-6 lg:mx-0 lg:px-0 pb-1">
          {CATEGORIES.map(c => {
            const active = category === c.id;
            const count = counts[c.id] ?? 0;
            return (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg text-left transition-colors flex-shrink-0
                  ${active
                    ? "bg-[var(--text-primary)] text-[var(--bg-primary)]"
                    : "text-[var(--text-secondary)] hover:bg-white hover:border-cream"
                  }`}
              >
                <span className="text-[13px] font-semibold whitespace-nowrap">{c.label}</span>
                <span className={`text-[11px] tabular-nums font-medium px-2 py-0.5 rounded-full
                  ${active ? "bg-white/15" : "bg-[var(--bg-secondary)]"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Main grid ── */}
      <div>
        {/* Header row: category blurb + search + count */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h2 className="text-[24px] font-extrabold tracking-tight mb-1">{activeCat.label}</h2>
            <p className="text-[14px] text-[var(--text-muted)]">{activeCat.blurb}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white border border-cream rounded-xl px-4 py-2.5 min-w-[240px]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--text-muted)] opacity-60">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search exams…"
                className="bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder-[var(--text-muted)] flex-1 min-w-0"
              />
            </div>
            <div className="flex items-center bg-white border border-cream rounded-xl overflow-hidden">
              <button
                onClick={() => setViewMode("grid")}
                title="Grid view"
                className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-[var(--text-primary)] text-[var(--bg-primary)]" : "text-[var(--text-muted)] hover:bg-[var(--bg-secondary)]"}`}
              >
                <LayoutGrid size={15} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                title="List view"
                className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-[var(--text-primary)] text-[var(--bg-primary)]" : "text-[var(--text-muted)] hover:bg-[var(--bg-secondary)]"}`}
              >
                <List size={15} />
              </button>
            </div>
          </div>
        </div>

        <div className="text-[12px] text-[var(--text-muted)] mb-4">
          Showing <span className="font-bold text-[var(--text-primary)]">{filtered.length}</span> {filtered.length === 1 ? "exam" : "exams"}
          {query.trim() && <> matching &ldquo;{query}&rdquo;</>}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white border border-cream rounded-xl p-12 text-center">
            <div className="text-[14px] text-[var(--text-muted)] mb-3">No exams match your search.</div>
            <button
              onClick={() => { setCategory("all"); setQuery(""); }}
              className="text-[13px] font-semibold text-indigo-brand hover:underline"
            >
              Clear filters →
            </button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="space-y-8">
            {tiers.live.length > 0 && (
              <div>
                <div className="text-[11px] font-bold text-emerald-700 mb-3 flex items-center gap-2">
                  <StatusDot /> COMPLETE — IDENTICAL TO THE REAL EXAM
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3.5">
                  {tiers.live.map(e => <ExamCard key={e.slug} e={e} />)}
                </div>
              </div>
            )}
            {tiers.beta.length > 0 && (
              <div>
                <div className="text-[11px] font-bold text-amber-700 mb-3 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-400" /> BETA — FULL RUNS, ONE POLISH LAYER PENDING
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3.5">
                  {tiers.beta.map(e => <ExamCard key={e.slug} e={e} />)}
                </div>
              </div>
            )}
            {tiers.soon.length > 0 && (
              <div>
                <div className="text-[11px] font-bold text-[var(--text-muted)] mb-3">COMING SOON</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3.5">
                  {tiers.soon.map(e => <ExamCard key={e.slug} e={e} />)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-cream bg-white border border-cream rounded-xl overflow-hidden">
            {filtered.map(e => {
              const status = statusOf(e.slug);
              const dimmed = status === "coming_soon";
              return (
                <Link
                  key={e.slug}
                  href={`/${e.slug}`}
                  className={`group flex items-center gap-4 px-5 py-3 hover:bg-[var(--bg-secondary)] transition-colors ${dimmed ? "opacity-70" : ""}`}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-extrabold tracking-tight"
                    style={{ background: e.abbrBg, color: e.abbrText, fontSize: e.abbr.length > 4 ? 10 : e.abbr.length === 4 ? 11 : 12 }}
                  >
                    {e.abbr}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14px] tracking-tight flex items-center gap-1.5">
                      <span>{e.name}</span>
                      <StatusPill status={status} />
                    </div>
                    <div className="text-[12px] text-[var(--text-muted)] truncate">{e.desc}</div>
                  </div>
                  <span className="flex-shrink-0 text-[11px] font-semibold text-[var(--text-muted)] tabular-nums ml-4">
                    {e.score}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-[var(--text-muted)] opacity-40 group-hover:opacity-80 transition-opacity">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Card used inside the tiered grid — status pill + honest beta caveat line. */
function ExamCard({ e }: { e: LandingExam }) {
  const status = statusOf(e.slug);
  const caveat = betaCaveat(e.slug);
  const dimmed = status === "coming_soon";
  return (
    <Link
      href={`/${e.slug}`}
      className={`group bg-white rounded-xl px-5 py-4 flex items-center gap-4 transition-all hover:border-indigo-brand hover:shadow-cream
        ${dimmed ? "opacity-70 border border-dashed border-[var(--border)]" : "border border-cream"}`}
    >
      <div
        className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center font-extrabold tracking-tight"
        style={{ background: e.abbrBg, color: e.abbrText, fontSize: e.abbr.length > 4 ? 11 : e.abbr.length === 4 ? 12 : 14 }}
      >
        {e.abbr}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-bold text-[15px] tracking-tight truncate min-w-0">{e.name}</span>
          <span className="flex-shrink-0"><StatusPill status={status} /></span>
        </div>
        <div className="text-[12px] text-[var(--text-muted)] truncate mt-0.5">
          {caveat ?? e.desc}
        </div>
      </div>
      <span className="flex-shrink-0 text-[11px] font-semibold text-[var(--text-muted)] tabular-nums">
        {dimmed ? "notify me" : e.score}
      </span>
    </Link>
  );
}
